import DefaultButton from 'components/common/DefaultButton';
import React from 'react';
import styled from 'styled-components';
import {minmax} from 'utils/minmax';
import {colors} from 'styles/colors';
import DefaultText from 'components/common/DefaultText';
import moment from 'moment-timezone';
import {IStatus} from 'interfaces/interface';
import {handleNormalDownloadClick} from 'function/converter';

/**
 * 공지사항 항목 컴포넌트의 Props 타입
 * @property {IStatus} data - 공지사항 데이터
 * @property {boolean} [isChildren] - 자식 항목 여부
 * @property {(data: IStatus, type: 'register' | 'modify' | 'delete') => void} onClickButton - 버튼 클릭 핸들러
 */
type Props = {
  data: IStatus;
  isChildren?: boolean;
  onClickButton: (
    data: IStatus,
    type: 'register' | 'modify' | 'delete',
  ) => void;
};

/**
 * 공지사항 항목 컴포넌트
 * 공지사항 항목을 표시하는 컴포넌트입니다.
 * @param {Props} props - 공지사항 항목 컴포넌트의 props
 * @returns {JSX.Element} 공지사항 항목 컴포넌트
 */
const NoticeItem = ({data, isChildren, onClickButton}: Props) => {
  const newCategoryStyle = {
    ...CategoryStyle,
    backgroundColor: isChildren ? colors.darkGray : colors.primary2,
  };
  return (
    <ContentItem key={data.uuid} style={{width: isChildren ? '90%' : '100%'}}>
      <ContentItemValues>
        <DefaultText text="상태명" textStyle={newCategoryStyle} />
        <DefaultText
          text={data.healthStatus || '일반 공지'}
          textStyle={ValueStyle}
        />
        <DefaultText text="제목" textStyle={newCategoryStyle} />
        <DefaultText text={data.title || '-'} textStyle={ValueStyle} />
        <DefaultText text="종료여부" textStyle={newCategoryStyle} />
        <DefaultText text={data.isClosed.toString()} textStyle={ValueStyle} />
        <DefaultText text="이슈생성시간" textStyle={newCategoryStyle} />
        <DefaultText
          text={data.issuedAt ? new Date(data.issuedAt).toISOString() : '-'}
          textStyle={ValueStyle}
        />
        <DefaultText text="생성시간" textStyle={newCategoryStyle} />
        <DefaultText
          text={data.createdAt ? new Date(data.createdAt).toISOString() : '-'}
          textStyle={ValueStyle}
        />

        <DefaultText text="종료시간" textStyle={newCategoryStyle} />
        <DefaultText
          text={data.closedAt ? new Date(data.closedAt).toISOString() : '-'}
          textStyle={ValueStyle}
        />
        <DefaultText text="종료예정시간" textStyle={newCategoryStyle} />
        <DefaultText
          text={
            data.estimatedCompletionTime
              ? new Date(data.estimatedCompletionTime).toISOString()
              : '-'
          }
          textStyle={ValueStyle}
        />
        <DefaultText text="업데이트시간" textStyle={newCategoryStyle} />
        <DefaultText
          text={data.updatedAt ? new Date(data.updatedAt).toISOString() : '-'}
          textStyle={ValueStyle}
        />
        {data.isNormalAnnouncement && data.files && data.files.length > 0 ? (
          <>
            <DefaultText text="첨부 파일" textStyle={newCategoryStyle} />
            <OneLineWrapper>
              {data.files.map(item => (
                <a
                  className="file"
                  key={item.srcURL}
                  onClick={async () =>
                    await handleNormalDownloadClick(item.srcURL, item.name)
                  }>
                  {item.name}
                </a>
              ))}
            </OneLineWrapper>
          </>
        ) : null}
        <DefaultText text="설명" textStyle={newCategoryStyle} />
        <OneLineWrapper>
          <DefaultText
            text={data.description || '-'}
            textStyle={{fontSize: minmax(23), whiteSpace: 'pre-wrap'}}
          />
        </OneLineWrapper>
      </ContentItemValues>

      <ContentItemGridButtons>
        {!isChildren && (
          <DefaultButton
            text="register"
            buttonStyle={{width: minmax(100), height: minmax(50)}}
            actived={1}
            onClick={() => onClickButton(data, 'register')}
          />
        )}
        <DefaultButton
          text="modify"
          buttonStyle={{width: minmax(100), height: minmax(50)}}
          actived={1}
          onClick={() => onClickButton(data, 'modify')}
        />
        <DefaultButton
          text="delete"
          buttonStyle={{width: minmax(100), height: minmax(50)}}
          actived={1}
          onClick={() => onClickButton(data, 'delete')}
        />
      </ContentItemGridButtons>
    </ContentItem>
  );
};

export default NoticeItem;

const ContentItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: ${minmax(10)};
`;
const ContentItemValues = styled.li`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 2fr;
  border-radius: ${minmax(10)};
  border: 1px solid ${colors.borderGray};
  border-bottom-width: 0;
  overflow: hidden;
`;

const CategoryStyle: React.CSSProperties = {
  backgroundColor: colors.primary2,
  color: colors.white,
  fontSize: minmax(23),
  padding: minmax(12),
  borderBottom: `1px solid ${colors.borderGray}`,
  borderRight: `1px solid ${colors.borderGray}`,
};

const OneLineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${minmax(12)};
  gap: ${minmax(8)};
  border-bottom: 1px solid ${colors.borderGray};
  grid-column: span 3;

  > .file {
    font-size: ${minmax(23)};
  }

  > .file:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ValueStyle: React.CSSProperties = {
  fontSize: minmax(23),
  padding: minmax(12),
  borderBottom: `1px solid ${colors.borderGray}`,
};

const ContentItemGridButtons = styled.li`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  column-gap: ${minmax(20)};
`;
